'use client'

import { getEndOfDate, now } from '@ftd/utils'
import { zodResolver } from '@hookform/resolvers/zod'
import { Accordion, Box, Group, SimpleGrid, Title } from '@mantine/core'
import { useTranslations } from 'next-intl'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { z } from 'zod'

import { useCreateSession } from '@/api/session/use-create-session'
import { ButtonSubmit } from '@/components/atoms/ButtonSubmit'
import { Form, FormColumn, Input } from '@/components/form'
import { DateTimePicker } from '@/components/form/DateTimePicker'
import { LocationsSelect } from '@/components/molecules/LocationsSelect'
import { useNotifications } from '@/hooks/useNotifications'

const createSessionSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  dateStart: z.date(),
  dateEnd: z.date(),
  location: z.string(),
})

type ICreateSessionForm = z.infer<typeof createSessionSchema>

type TCreateSessionForm = {
  onSubmit: SubmitHandler<ICreateSessionForm>
  isMobile?: boolean
}

const SessionForm: React.FC<TCreateSessionForm> = ({ onSubmit, isMobile }) => {
  const t = useTranslations('session')
  const createSession = useCreateSession()
  const today = now()
  const todayEndOfDay = getEndOfDate(today)

  const { register, handleSubmit, control, formState, setValue, watch } = useForm<ICreateSessionForm>({
    resolver: zodResolver(createSessionSchema),
    mode: 'onChange',
    defaultValues: {
      name: '',
      dateStart: today,
      dateEnd: todayEndOfDay,
      location: '',
    },
  })
  const [location] = watch(['location'])
  const { isValid, errors: formErrors } = formState

  const formContent = (
    <SimpleGrid
      spacing="sm"
      cols={{
        base: 1,
        sm: isMobile ? 1 : 5,
      }}
    >
      <FormColumn>
        <Input
          label={t('name_label')}
          error={formErrors?.name?.message}
          placeholder={t('name_placeholder')}
          w="100%"
          {...register('name')}
        />
      </FormColumn>

      <FormColumn>
        <Controller
          name="dateStart"
          control={control}
          render={({ field: { onChange, value } }) => (
            <DateTimePicker
              label={t('start_date_label')}
              onValueChange={onChange}
              value={value}
              error={formErrors?.dateStart?.message}
            />
          )}
        />
      </FormColumn>

      <FormColumn>
        <Controller
          name="dateEnd"
          control={control}
          render={({ field: { onChange, value } }) => (
            <DateTimePicker
              label={t('end_date_label')}
              onValueChange={onChange}
              value={value}
              error={formErrors?.dateEnd?.message}
              minDate={watch('dateStart')}
            />
          )}
        />
      </FormColumn>

      <FormColumn>
        <LocationsSelect
          label={t('location')}
          createEnabled
          onLocationSelect={(location) => setValue('location', location?._id ?? '')}
          error={formErrors?.location?.message}
        />
      </FormColumn>
      <FormColumn justify="end" mt={isMobile ? 0 : 'md'}>
        <ButtonSubmit disabled={!isValid || createSession.isPending || !location} size="md">
          {t('submit_button')}
        </ButtonSubmit>
      </FormColumn>
    </SimpleGrid>
  )

  return <Form onSubmit={handleSubmit(onSubmit)}>{formContent}</Form>
}

export const CreateSession: React.FC = () => {
  const t = useTranslations('session')
  const { notifySuccess, notifyError } = useNotifications()
  const createSession = useCreateSession()

  const handleCreateSession: SubmitHandler<ICreateSessionForm> = async (values) => {
    if (!values.dateStart || !values.dateEnd) return

    const newSession = {
      name: values.name,
      dateStart: values.dateStart,
      dateEnd: values.dateEnd,
      location: values.location,
    }

    try {
      await createSession.mutateAsync(newSession)
      notifySuccess(t('created_successfully'))
    } catch {
      notifyError(t('creation_failed'))
    }
  }

  return (
    <>
      {/* Mobile View */}
      <Box hiddenFrom="sm">
        <Accordion variant="contained">
          <Accordion.Item value="create-session">
            <Accordion.Control>
              <Title order={3}>{t('create_new_session')}</Title>
            </Accordion.Control>
            <Accordion.Panel>
              <SessionForm onSubmit={handleCreateSession} isMobile />
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      </Box>

      {/* Desktop View */}
      <Box visibleFrom="sm">
        <Group align="center" justify="center" gap="md" w="100%">
          <Title order={2}>{t('create_new_session')}</Title>
          <SessionForm onSubmit={handleCreateSession} />
        </Group>
      </Box>
    </>
  )
}
